import axios from 'axios';
import { DateTime } from 'luxon';
import { ApiError } from '@/models/errors/ApiError';
import { downloadBlob } from '@/utils/csvExport';
import { axiosClient } from './apiClient';

/** The backend queued the export and will email a signed download link. */
export type ExportQueuedResult = {
    mode: 'queued';
    id: number;
    status: string;
    rowCountEstimate: number;
    message: string;
};

/** The CSV came back inline and has already been handed to the browser. */
export type ExportSyncResult = {
    mode: 'sync';
    filename: string;
};

export type ExportResult = ExportQueuedResult | ExportSyncResult;

const DEFAULT_QUEUED_MESSAGE = 'Your export is being prepared. We will email you a download link when it is ready.';

/** Sync CSVs can be large, so allow well past the default request timeout. */
const EXPORT_TIMEOUT_MS = 150000;

/** `yyyy-MM-dd` → `MMddyyyy`, falling back to today. Mirrors the backend filename helper. */
export function formatExportFilenameDate(date: string | undefined): string {
    if (!date || date.trim() === '') {
        return DateTime.now().toFormat('MMddyyyy');
    }

    const parsed = DateTime.fromFormat(date.trim(), 'yyyy-MM-dd');
    return parsed.isValid ? parsed.toFormat('MMddyyyy') : DateTime.now().toFormat('MMddyyyy');
}

/**
 * With `responseType: 'blob'` even JSON error bodies arrive as Blobs, so the message has to be
 * read back out before it can be surfaced.
 */
async function readBlobErrorMessage(data: unknown): Promise<string> {
    if (!(data instanceof Blob)) {
        if (data && typeof data === 'object' && 'message' in data && typeof (data as { message: unknown }).message === 'string') {
            return (data as { message: string }).message;
        }
        return 'Request failed';
    }

    try {
        const parsed = JSON.parse(await data.text()) as { message?: string };
        if (typeof parsed.message === 'string' && parsed.message.trim() !== '') {
            return parsed.message;
        }
    } catch {
        // Not JSON — fall through to the generic message.
    }

    return 'Request failed';
}

/**
 * Posts a CSV export request that answers with either a file (sync) or a `202` job (queued).
 *
 * Uses the raw axios client rather than `apiRequest` because the response shape is not known
 * up-front: the same endpoint returns a CSV blob or a JSON job envelope depending on size.
 */
export async function requestCsvExport(
    url: string,
    body: Record<string, unknown>,
    filename: string,
): Promise<ExportResult> {
    try {
        const response = await axiosClient.request<Blob>({
            url,
            method: 'POST',
            data: body,
            responseType: 'blob',
            timeout: EXPORT_TIMEOUT_MS,
            headers: {
                'Content-Type': 'application/json',
            },
        });

        const contentType = String(response.headers['content-type'] ?? '');
        if (response.status === 202 || contentType.includes('application/json')) {
            const json = JSON.parse(await response.data.text()) as {
                data?: { id?: number; status?: string; row_count_estimate?: number };
                message?: string;
            };

            return {
                mode: 'queued',
                id: Number(json.data?.id ?? 0),
                status: String(json.data?.status ?? 'queued'),
                rowCountEstimate: Number(json.data?.row_count_estimate ?? 0),
                message: typeof json.message === 'string' ? json.message : DEFAULT_QUEUED_MESSAGE,
            };
        }

        downloadBlob(filename, response.data);

        return { mode: 'sync', filename };
    } catch (error) {
        if (axios.isAxiosError(error) && error.response) {
            const message = await readBlobErrorMessage(error.response.data);
            throw new ApiError(
                error.response.statusText || 'Request failed',
                error.response.status,
                { message },
            );
        }

        throw error;
    }
}
